import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivationEnd, NavigationEnd, NavigationStart, Router } from "@angular/router";
import { Subject } from 'rxjs';
import { take, takeUntil } from "rxjs/operators";
import { Meta, Title } from "@angular/platform-browser";
import { TranslateService } from '@ngx-translate/core';
import { MetadataEntityModels } from '@medsurf/flat-models';
import * as FlatServices from '@medsurf/flat-services';
import * as FlatFacades from '@medsurf/flat-facades';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
    selector: 'medsurf-app',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  /**
   * Members
   */
  #title?: string;
  private _destroyed$ = new Subject<boolean>();
  private metadata: MetadataEntityModels.Metadata = null;
  public isLoader: boolean;

  /**
   * Constructor
   *
   * @param router
   * @param meta
   * @param translate
   * @param mediaControlService
   * @param feedbackControlService
   * @param modalControlService
   * @param navigationControlService
   * @param matomoControlService
   * @param appSharedFacade
   * @param menuSharedFacade
   * @param navigationControlFacade
   * @param slideViewerFacade
   * @param titleService
   * @param settings
   */
  public constructor(public router: Router,
                     private meta: Meta,
                     public translate: TranslateService,
                     public mediaControlService: FlatServices.MediaControlService,
                     public feedbackControlService: FlatServices.FeedbackControlService,
                     public modalControlService: FlatServices.ModalControlService,
                     public navigationControlService: FlatServices.NavigationControlService,
                     public matomoControlService: FlatServices.MatomoControlService,
                     public authContorlService: FlatServices.AuthControlService,
                     public appSharedFacade: FlatFacades.AppSharedFacade,
                     public menuSharedFacade: FlatFacades.MenuSharedFacade,
                     public navigationControlFacade: FlatFacades.NavigationControlFacade,
                     public slideViewerFacade: FlatFacades.SlideViewerFacade,
                     public titleService: Title,
                     @Inject(FlatServices.FLAT_SETTINGS) private settings: FlatServices.SettingControlModel) {
    this.isLoader = true;

    translate.setDefaultLang('de');
    translate.use('de');
    
    this.router.events.pipe(takeUntilDestroyed()).subscribe((val) => {
      if (val instanceof ActivationEnd) {
        this.#title = undefined;
        if (val.snapshot.data.title) {
          this.#title = val.snapshot.data.title;
        } else {
          this.#title = this.appSharedFacade.snapshot_currentSelectedSlideTitle();
          if (!this.#title) {
            this.#title = this.slideViewerFacade.snapshot_currentSelectedSlidePage()?.header;
          }
        }
        if (!this.#title) {
          this.#title = this.appSharedFacade.snapshot_metadata()?.title || 'upps';
        }
        this.titleService.setTitle(this.translate.instant(this.#title));
      }
    });
    
    this.translate.onDefaultLangChange.pipe(take(1)).subscribe(_ => {
      if (this.#title) {
        this.titleService.setTitle(this.translate.instant(this.#title));
      }
    });
  }

  ngOnInit(): void {
    this.meta.updateTag({ property: 'og:locale', content: 'de_CH' });

    /* TODO meta ???
    const order = [...NavigationControlState.order].reverse();
    this.currentPath$.pipe(filter(path => !!path), takeUntil(this._destroyed$))
      .subscribe((path: NavigationControlModels.PathModel) => {
 
      const nodes: Node[] = order.map(key => {
        return path[key];
      }).filter(node => !!node);

      const currentNode = nodes[0] || null;
      const parentNode = nodes[1] || null;
      const type = (nodes?.[0] as any)?.page?.type;

      const metaTags = {
        title: (currentNode as any)?.page?.title || this.metadata?.title || '',
        type: [PageEntityModels.PageType.SLIDE, PageEntityModels.PageType.TRAINING].includes(type) ? 'article' : 'website',
        media: this.metadata?.carouselMedia,
        description: this.metadata?.description,
        keywords: this.metadata?.keywords
      };

      const span = document.createElement('span');
      if ([PageEntityModels.PageType.SLIDE, PageEntityModels.PageType.TRAINING].includes(type)) {
        span.innerHTML = ((currentNode as any)?.page as SlideEntityModels.Slide)?.body || '';
      } else {
        span.innerHTML = ((currentNode as any)?.page as PageEntityModels.Folder)?.description || '';
      }
      const description = span.querySelector('p, span')?.textContent?.trim();
      if (description) {
        metaTags.description = description;
      }

      if ([PageEntityModels.PageType.SLIDE, PageEntityModels.PageType.TRAINING].includes(type)) {
        metaTags.media = ((currentNode as any)?.page as SlideEntityModels.Slide)?.thumbnailMedia
          || metaTags.media;
      } else {
        metaTags.media = ((currentNode as any)?.page as PageEntityModels.Folder)?.media
          || ((parentNode as any)?.page as PageEntityModels.Folder)?.media || metaTags.media;
      }

      this.meta.updateTag({property: 'og:title', content: metaTags.title });
      this.meta.updateTag({property: 'og:type', content: metaTags.type });
      this.meta.updateTag({property: 'og:description', content: metaTags.description });
      if (metaTags.media) {
        this.meta.updateTag({
          property: 'og:image', content: this.mediaControlService.getUrl(metaTags.media, 'thumbnail')
            || this.mediaControlService.getUrl(metaTags.media)
        });
      } else {
        this.meta.removeTag("property = 'og:image");
      }

      this.meta.updateTag({name: 'keywords', content: metaTags.keywords});
      this.meta.updateTag({property: 'description', content: metaTags.description});
    });
    */
    this.appSharedFacade.requestMetadata();
    this.appSharedFacade.metadata$.pipe(takeUntil(this._destroyed$)).subscribe((metadata) => {
      this.metadata = metadata;
    });

    this.router.events.pipe(takeUntil(this._destroyed$)).subscribe((val) => {
      if (val instanceof NavigationEnd) {

        // Used to redirect after login
        if (!val.urlAfterRedirects.startsWith('/login')) {
          this.authContorlService.setRedirectUrl(window.location.href);
        }
        this.isLoader = false;
        this.meta.updateTag({ property: 'og:url', content: window.location.href });

        setTimeout(() => {
          this.matomoControlService.trackPageView(val.url, document.title);
        }, 300);
      }
      if (val instanceof NavigationStart) {
        this.slideViewerFacade.requestClearEvents();
      }
    });
  }

  /**
   * Ng On Destroy
   */
  ngOnDestroy(): void {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }
}
